import { Timer } from "lucide-react";
import { useEffect, useState } from "react";

export type AgentTime = {
  /** The agent's working seconds at `measuredAt`. */
  seconds: number;
  running: boolean;
  /** When the server measured `seconds`, in epoch milliseconds. */
  measuredAt: number;
};

export const formatDuration = (totalSeconds: number) => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds % 60)}`
    : `${minutes}:${pad(seconds % 60)}`;
};

const useNow = (ticking: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [ticking]);
  return now;
};

/** The agent's working time. It counts only while the agent runs. */
export const AgentTimer = ({ seconds, running, measuredAt }: AgentTime) => {
  const now = useNow(running);
  const elapsed = seconds + (running ? Math.max(0, now - measuredAt) / 1_000 : 0);
  return (
    <span
      role="timer"
      title="The agent's working time. It stops while the agent waits for you."
      className="inline-flex items-center gap-1 text-xs tabular-nums text-zinc-500"
    >
      <Timer className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">Agent working time: </span>
      {formatDuration(elapsed)}
    </span>
  );
};
